/**
 * Timestamped, signed notes on any record — decision 91.
 *
 * *"The lead pattern, everywhere: a timestamped activity line, never a free-text
 * blob. Every note carries date and author and the newest sits on top."*
 *
 * Sales and marketing reported the gap twice on 20 Aug 2026 — AVV-2026-002 on
 * partners (*"vanskelig å legge til nye notater om partner eller dialoger med
 * partner"*) and AVV-2026-003 on companies (*"Notes m/ New note og time stamp,
 * og gjerne navn på den som har skrevet notatet"*). Both describe exactly what
 * 91 specifies, which is a good sign the decision was right and a bad sign about
 * how long it sat unbuilt.
 *
 * ## Why a collection rather than a field on each record
 *
 * A `notes` string is what partners had, and it is the thing 91 rules out: two
 * people editing it overwrite each other, nothing says who wrote what or when,
 * and the only way to add to it is to retype what is already there. That is what
 * "vanskelig å legge til" means in practice.
 *
 * One append-only collection keyed by `(kind, recordId)` gives every record the
 * same log without a schema change per feature, and it is append-only in the
 * rules as well as by habit: a note is a record of what somebody knew at a time,
 * so editing one rewrites history rather than correcting it. A mistake is
 * answered with another note.
 *
 * ## Scope, which is deliberate
 *
 * Notes on a **shareholder** are internal: 91 keeps them out of the
 * aksjeeierbok and out of every generated document. Nothing here writes to those
 * documents, so that holds by construction rather than by remembering — but it
 * is why `kind` exists as a closed union rather than a free string. A new kind is
 * a decision, not a typo.
 */

/** Which record a note hangs off. Closed on purpose — see the note above. */
export type NoteKind = 'partner' | 'proCompany' | 'event' | 'ambassador' | 'shareholder'

export interface Note {
  id: string
  kind: NoteKind
  /** The record's own id, within its kind. */
  recordId: string
  /** ISO timestamp, from the server rather than the browser's clock. */
  at: string
  /** Who wrote it, as their display name read at the time. */
  author: string
  text: string
  /**
   * `migrated` marks the prose that used to live in a `notes` field.
   *
   * 91: *"Partners had one prose `notes` string — it survives as the first entry
   * in the log rather than being discarded."* Marking it says why a note has no
   * real author and a made-up date, instead of leaving somebody to wonder who
   * "—" is.
   */
  source: 'user' | 'migrated'
}

/** Firestore rejects an empty string and a runaway paste equally usefully. */
export const NOTE_MAX = 5000
